<!-- RecordingCountdown.vue -->
<template>
    <transition name="countdown-fade">
      <div v-if="visible" class="countdown-overlay">
        <div class="countdown-content">
          <div class="countdown-container">
            <!-- Countdown number with animation -->
            <transition name="number-scale" mode="out-in">
              <div v-if="count > 0" :key="count" class="countdown-number">
                {{ count }}
              </div>
              <div v-else class="recording-starting">
                <div class="recording-icon">
                  <div class="recording-dot"></div>
                </div>
                <span>Recording</span>
              </div>
            </transition>

            <!-- Instructions -->
            <div class="countdown-instructions">
              <h3>Get Ready to Record</h3>
              <ul>
                <li>Move your mouse to the edge of the screen</li>
                <li>Recording will start automatically</li>
                <li>Click <kbd>Stop Sharing</kbd> to stop recording</li>
              </ul>
            </div>

            <!-- Progress ring -->
            <svg class="countdown-progress" viewBox="0 0 120 120">
              <circle
                cx="60"
                cy="60"
                r="54"
                fill="none"
                stroke="rgba(255, 255, 255, 0.1)"
                stroke-width="8"
              />
              <circle
                cx="60"
                cy="60"
                r="54"
                fill="none"
                stroke="rgba(255, 255, 255, 0.9)"
                stroke-width="8"
                :stroke-dasharray="circumference"
                :stroke-dashoffset="strokeDashoffset"
                transform="rotate(-90 60 60)"
              />
            </svg>
          </div>

          <!-- Cancel button -->
          <button @click="cancelCountdown" class="cancel-countdown">
            Cancel
          </button>
        </div>
      </div>
    </transition>
  </template>

  <script>
  export default {
    name: 'RecordingCountdown',
    props: {
      duration: {
        type: Number,
        default: 3
      }
    },
    data() {
      return {
        visible: false,
        count: this.duration,
        timer: null,
        startTime: null,
        animationFrame: null,
        circumference: 2 * Math.PI * 54 // radius = 54
      };
    },
    computed: {
      strokeDashoffset() {
        if (this.count === 0) return this.circumference;
        const progress = (this.duration - this.count) / this.duration;
        return this.circumference * (1 - progress);
      }
    },
    methods: {
      start() {
        this.visible = true;
        this.count = this.duration;
        this.startTime = Date.now();

        // Play countdown sound if available
        this.playCountdownSound();

        // Start the countdown
        this.timer = setInterval(() => {
          const elapsed = Math.floor((Date.now() - this.startTime) / 1000);
          this.count = Math.max(0, this.duration - elapsed);

          if (this.count === 0) {
            this.complete();
          } else {
            this.playCountdownSound();
          }
        }, 1000);

        // Smooth progress animation
        this.animateProgress();
      },

      animateProgress() {
        const animate = () => {
          if (!this.visible) return;

          const elapsed = (Date.now() - this.startTime) / 1000;
          const progress = Math.min(elapsed / this.duration, 1);

          // Update the progress smoothly
          this.$el.style.setProperty('--progress', progress);

          if (progress < 1) {
            this.animationFrame = requestAnimationFrame(animate);
          }
        };

        this.animationFrame = requestAnimationFrame(animate);
      },

      complete() {
        clearInterval(this.timer);
        cancelAnimationFrame(this.animationFrame);

        // Show "Recording" for a brief moment
        setTimeout(() => {
          this.visible = false;
          this.$emit('complete');
        }, 500);
      },

      cancelCountdown() {
        clearInterval(this.timer);
        cancelAnimationFrame(this.animationFrame);
        this.visible = false;
        this.$emit('cancelled');
      },

      playCountdownSound() {
        // Simple beep sound using Web Audio API
        try {
          const audioContext = new (window.AudioContext || window.webkitAudioContext)();
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();

          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);

          oscillator.frequency.value = this.count === 1 ? 880 : 440; // Higher pitch on last count
          oscillator.type = 'sine';

          gainNode.gain.setValueAtTime(0, audioContext.currentTime);
          gainNode.gain.linearRampToValueAtTime(0.1, audioContext.currentTime + 0.01);
          gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);

          oscillator.start(audioContext.currentTime);
          oscillator.stop(audioContext.currentTime + 0.1);
        } catch (e) {
          // Silently fail if Web Audio API is not available
        }
      }
    },
    beforeDestroy() {
      clearInterval(this.timer);
      cancelAnimationFrame(this.animationFrame);
    }
  };
  </script>

  <style scoped>
  .countdown-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.9);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    backdrop-filter: blur(8px);
  }

  .countdown-content {
    position: relative;
    text-align: center;
    color: white;
  }

  .countdown-container {
    position: relative;
    width: 300px;
    height: 300px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .countdown-number {
    font-size: 120px;
    font-weight: bold;
    line-height: 1;
    margin: 0;
    position: relative;
    z-index: 2;
    text-shadow: 0 0 20px rgba(255, 255, 255, 0.5);
  }

  .recording-starting {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    position: relative;
    z-index: 2;
  }

  .recording-icon {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background: rgba(220, 38, 38, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .recording-dot {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: #dc2626;
    animation: pulse 1.5s ease-in-out infinite;
  }

  .recording-starting span {
    font-size: 24px;
    font-weight: 600;
    color: white;
  }

  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
      opacity: 0.8;
    }
    50% {
      transform: scale(1.1);
      opacity: 1;
    }
  }

  .countdown-instructions {
    position: absolute;
    bottom: -120px;
    left: 50%;
    transform: translateX(-50%);
    text-align: center;
    width: 100%;
  }

  .countdown-instructions h3 {
    margin: 0 0 12px 0;
    font-size: 20px;
    font-weight: 600;
    opacity: 0.9;
  }

  .countdown-instructions ul {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .countdown-instructions li {
    font-size: 16px;
    opacity: 0.8;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
  }

  .countdown-instructions kbd {
    background: rgba(255, 255, 255, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 4px;
    padding: 2px 6px;
    font-family: monospace;
    font-size: 14px;
    box-shadow: 0 2px 0 rgba(0, 0, 0, 0.2);
  }

  .countdown-progress {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 240px;
    height: 240px;
    z-index: 1;
  }

  .countdown-progress circle {
    transition: stroke-dashoffset 0.1s linear;
  }

  .cancel-countdown {
    position: absolute;
    bottom: 40px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: white;
    padding: 10px 24px;
    border-radius: 24px;
    font-size: 16px;
    cursor: pointer;
    transition: all 0.2s ease;
    backdrop-filter: blur(4px);
  }

  .cancel-countdown:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateX(-50%) scale(1.05);
  }

  /* Transitions */
  .countdown-fade-enter-active,
  .countdown-fade-leave-active {
    transition: opacity 0.3s ease;
  }

  .countdown-fade-enter,
  .countdown-fade-leave-to {
    opacity: 0;
  }

  .number-scale-enter-active,
  .number-scale-leave-active {
    transition: all 0.3s ease;
  }

  .number-scale-enter {
    opacity: 0;
    transform: scale(1.5);
  }

  .number-scale-leave-to {
    opacity: 0;
    transform: scale(0.5);
  }

  /* Responsive adjustments */
  @media (max-width: 768px) {
    .countdown-container {
      width: 200px;
      height: 200px;
    }

    .countdown-number {
      font-size: 80px;
    }

    .countdown-progress {
      width: 160px;
      height: 160px;
    }

    .countdown-instructions {
      bottom: -100px;
    }

    .countdown-instructions h3 {
      font-size: 18px;
    }

    .countdown-instructions li {
      font-size: 14px;
    }
  }
  </style>
